import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import { Close, Receipt, Business, Category, AttachMoney } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { expenseService, CreateExpenseData, UpdateExpenseData, Expense } from '../../services/expenseService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';

interface ExpenseFormProps {
  open: boolean;
  onClose: () => void;
  expense?: Expense | null;
  onSuccess: () => void;
}

const paymentMethods = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'UPI', label: 'UPI' },
  { value: 'ONLINE', label: 'Online Transfer' },
];

const expenseCategories = [
  'Office Supplies',
  'Utilities',
  'Rent',
  'Marketing',
  'Inventory',
  'Equipment',
  'Maintenance',
  'Professional Services',
  'Travel',
  'Insurance',
  'Staff Expenses',
  'Other',
];

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  open,
  onClose,
  expense,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>(expenseCategories);
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    gstAmount: '',
    paymentMethod: '',
    receiptNumber: '',
    vendorName: '',
    expenseDate: dayjs() as Dayjs,
  });

  const isEdit = Boolean(expense);

  useEffect(() => {
    if (open) {
      loadVendors();
      loadCategories();
      
      if (expense) {
        setFormData({
          category: expense.category,
          description: expense.description,
          amount: expense.amount.toString(),
          gstAmount: expense.gstAmount?.toString() || '',
          paymentMethod: expense.paymentMethod,
          receiptNumber: expense.receiptNumber || '',
          vendorName: expense.vendorName || '',
          expenseDate: dayjs(expense.expenseDate),
        });
      } else {
        resetForm();
      }
    }
  }, [open, expense]);

  const loadVendors = async () => {
    try {
      const vendorList = await expenseService.getVendors();
      setVendors(vendorList);
    } catch (error) {
      console.error('Failed to load vendors:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const categoryList = await expenseService.getExpenseCategories();
      if (categoryList.length > 0) {
        setCategories([...new Set([...expenseCategories, ...categoryList])]);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      category: '',
      description: '',
      amount: '',
      gstAmount: '',
      paymentMethod: '',
      receiptNumber: '',
      vendorName: '',
      expenseDate: dayjs(),
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.category.trim()) {
      dispatch(addNotification({
        type: 'error',
        message: 'Category is required',
      }));
      return false;
    }

    if (!formData.description.trim()) {
      dispatch(addNotification({
        type: 'error',
        message: 'Description is required',
      }));
      return false;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      dispatch(addNotification({
        type: 'error',
        message: 'Valid amount is required',
      }));
      return false;
    }

    if (!formData.paymentMethod) {
      dispatch(addNotification({
        type: 'error',
        message: 'Payment method is required',
      }));
      return false;
    }

    if (!formData.expenseDate) {
      dispatch(addNotification({
        type: 'error',
        message: 'Expense date is required',
      }));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const expenseData = {
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount),
        gstAmount: formData.gstAmount ? parseFloat(formData.gstAmount) : undefined,
        paymentMethod: formData.paymentMethod,
        receiptNumber: formData.receiptNumber || undefined,
        vendorName: formData.vendorName || undefined,
        expenseDate: formData.expenseDate.toISOString(),
      };

      if (isEdit && expense) {
        await expenseService.updateExpense(expense.id, expenseData as UpdateExpenseData);
        dispatch(addNotification({
          type: 'success',
          message: 'Expense updated successfully',
        }));
      } else {
        await expenseService.createExpense(expenseData as CreateExpenseData);
        dispatch(addNotification({
          type: 'success',
          message: 'Expense created successfully',
        }));
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      dispatch(addNotification({
        type: 'error',
        message: error.message || 'Failed to save expense',
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: '#E8B4B8',
                  color: 'white',
                }}
              >
                <Receipt />
              </Box>
              <Typography variant="h6" fontWeight={700}>
                {isEdit ? 'Edit Expense' : 'Add New Expense'}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={categories}
                value={formData.category}
                onChange={(_, value) => handleInputChange('category', value || '')}
                onInputChange={(_, value) => handleInputChange('category', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    required
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Category sx={{ color: '#E8B4B8' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  label="Payment Method"
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                required
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter expense description..."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Amount"
                required
                fullWidth
                type="number"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney sx={{ color: '#E8B4B8' }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="GST Amount"
                fullWidth
                type="number"
                value={formData.gstAmount}
                onChange={(e) => handleInputChange('gstAmount', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney sx={{ color: '#E8B4B8' }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="Optional"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={vendors}
                value={formData.vendorName}
                onChange={(_, value) => handleInputChange('vendorName', value || '')}
                onInputChange={(_, value) => handleInputChange('vendorName', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor Name"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business sx={{ color: '#E8B4B8' }} />
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Optional"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Receipt Number"
                fullWidth
                value={formData.receiptNumber}
                onChange={(e) => handleInputChange('receiptNumber', e.target.value)}
                placeholder="Optional"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Expense Date"
                value={formData.expenseDate}
                onChange={(date) => handleInputChange('expenseDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: '#E8B4B8',
              color: '#E8B4B8',
              '&:hover': {
                borderColor: '#D4969B',
                bgcolor: 'rgba(232, 180, 184, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: '#E8B4B8',
              color: 'white',
              '&:hover': {
                bgcolor: '#D4969B',
              },
              '&:disabled': {
                bgcolor: 'rgba(232, 180, 184, 0.5)',
              },
            }}
          >
            {loading ? 'Saving...' : isEdit ? 'Update Expense' : 'Create Expense'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ExpenseForm;